import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import type { User } from '@/lib/api';
import { AuthContext, type GoogleLoginResult } from '@/contexts/auth-context';
import { trackEvent } from '@/lib/analytics';
import i18n from '@/lib/i18n';
import { GoogleSignInError, getGoogleIdToken } from '@/lib/google-signin';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    console.log('[AuthContext] refreshUser start');
    if (!api.getToken()) {
      console.log('[AuthContext] No token present, treating as logged out');
      setUser(null);
      setIsLoading(false);
      return;
    }

    // The session this read is for. A sign-in from the Telegram studio
    // (Pricing, ?signin=) can replace the token while this read is still in
    // flight -- an old or expired session answering late must then neither
    // wipe the new token nor put the old account back on screen.
    const tokenAtStart = api.getToken();
    const result = await api.getCurrentUser();
    if (api.getToken() !== tokenAtStart) {
      // Whoever changed the token loads its user; this read only stops blocking.
      setIsLoading(false);
      return;
    }
    if (result.success && result.data) {
      console.log('[AuthContext] Current user loaded');
      setUser(result.data);
    } else {
      console.warn('[AuthContext] Failed to load current user, clearing session', { error: result.error });
      setUser(null);
      api.setToken(null);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    console.log('[AuthContext] login called');
    const result = await api.login({ email, password });
    if (result.success && result.data) {
      console.log('[AuthContext] login success, updating user state');
      setUser(result.data.user);
      trackEvent('login_completed', { userId: result.data.user.id });
      return { success: true };
    }
    console.warn('[AuthContext] login failed', { error: result.error });
    return { success: false, error: result.error };
  };

  const signup = async (email: string, password: string, name?: string, promoCode?: string) => {
    console.log('[AuthContext] signup called');
    const result = await api.signup({
      email,
      password,
      name,
      promo_code: promoCode?.trim() || undefined,
      lang: i18n.language,
    });
    if (result.success && result.data) {
      console.log('[AuthContext] signup success, updating user state');
      setUser(result.data.user);
      trackEvent('signup_completed', {
        userId: result.data.user.id,
        promoStatus: result.data.promo?.status ?? null,
      });
      return {
        success: true,
        promo: result.data.promo ?? null,
        verificationEmailSent: result.data.verification_email_sent === true,
      };
    }
    console.warn('[AuthContext] signup failed', { error: result.error });
    return { success: false, error: result.error };
  };

  // "Continue with Google" (2026-09-25). The popup has to open inside the tap,
  // so getGoogleIdToken() is the FIRST statement: no await and no state
  // update may run before it. The session it ends in is the site's own JWT,
  // stored like a password login's; setUser is all the pages need (Login /
  // Signup and PublicRoute then follow redirect_to as they do for a password).
  // Never rememberTelegramSession: this is a website account, not a Telegram one.
  const loginWithGoogle = async (): Promise<GoogleLoginResult> => {
    const pendingIdToken = getGoogleIdToken();
    let idToken: string;
    try {
      idToken = await pendingIdToken;
    } catch (err) {
      const code = err instanceof GoogleSignInError
        ? (err.tapAgain ? 'tap_again' : err.code)
        : 'unknown';
      if (code !== 'cancelled') {
        console.warn('[AuthContext] Google popup failed', {
          code,
          firebaseCode: err instanceof GoogleSignInError ? err.firebaseCode : null,
        });
      }
      return { success: false, code, error: code };
    }

    const result = await api.googleSignIn(idToken, i18n.language);
    if (result.success && result.data?.user) {
      const isNewUser = result.data.is_new_user === true;
      setUser(result.data.user);
      trackEvent(isNewUser ? 'signup_completed' : 'login_completed', {
        userId: result.data.user.id,
        method: 'google',
      });
      return { success: true, isNewUser };
    }
    // The server's code when it answered with one; no answer at all (offline,
    // timed out) is a network problem.
    const serverCode = typeof result.body?.error === 'string' ? result.body.error : null;
    const code = serverCode || (result.status ? 'google_signin_failed' : 'network');
    console.warn('[AuthContext] Google sign-in refused', { code, status: result.status ?? null });
    return { success: false, code, error: result.error || code };
  };

  const logout = () => {
    console.log('[AuthContext] logout called');
    trackEvent('logout_completed', { userId: user?.id ?? null });
    api.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        signup,
        loginWithGoogle,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}


